import {
  type FormEvent,
  useMemo,
  useState,
  useEffect,
  useRef,
  memo
} from "react";
import { motion, Variants, type Transition } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Layout";
import { getCsrfToken } from "../utils/csrf";
import ConvoHero from "@/components/hero/ConvoHero";
import type { ReactElement } from "react";

const FEATURE_CONFIG = [
  { key: "multilingual" as const, icon: "fa-language" },
  { key: "voice" as const, icon: "fa-microphone-lines" },
  { key: "responses" as const, icon: "fa-robot" },
  { key: "workflows" as const, icon: "fa-diagram-project" },
  { key: "security" as const, icon: "fa-shield-halved" },
  { key: "integration" as const, icon: "fa-plug" }
] as const;

type FeatureKey = (typeof FEATURE_CONFIG)[number]["key"];


/* BRAND & DECK STYLES */
const BRAND = {
  indigo: "#2F2A7A",
  teal: "#00B7B0",
  text: "#2F2A7A",
  panelTop: "#F3F8FF",
  panelBottom: "#ECFBF8",
  chipBg: "#ECF5F4",
  chipText: "#155E59"
};

const deckContainer: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } }
};

function useDeckTokens() {
  const [w, setW] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR, { passive: true });
    return () => window.removeEventListener("resize", onR);
  }, []);
  const t = useMemo(() => {
    if (w < 600) return { cw: 300, ch: 360 };
    if (w < 1024) return { cw: 330, ch: 390 };
    if (w < 1440) return { cw: 360, ch: 410 };
    return { cw: 380, ch: 430 };
  }, [w]);
  return t;
}

/* Icons */
const Icon = {
  Globe: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3 12h18M12 3c3.5 4 3.5 14 0 18M12 3c-3.5 4-3.5 14 0 18" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  Mic: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="9" y="3" width="6" height="10" rx="3" stroke="currentColor" strokeWidth="1.7" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  Flow: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="6" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="18" cy="6" r="2.2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="18" cy="18" r="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.5 6h5a3.5 3.5 0 0 1 3.5 3.5V18" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  Spark: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3v6M12 21v-6M3 12h6M21 12h-6M6 6l4 4M18 6l-4 4M6 18l4-4M18 18l-4-4" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  Shield: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-4z" stroke="currentColor" strokeWidth="1.7" fill="none" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  Plug: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 8v4a5 5 0 0 0 10 0V8M9 3v5M15 3v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 17v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
};

const glassBg = "linear-gradient(135deg, rgba(47,42,122,.92) 0%, rgba(0,183,176,.90) 100%)";

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="h-full w-full rounded-2xl p-4 flex items-center justify-center"
      style={{
        background: glassBg,
        boxShadow: "0 22px 40px rgba(20,25,60,.14)",
        backdropFilter: "saturate(130%) blur(6px)"
      }}
    >
      <div className="w-full rounded-xl bg-white/92 shadow-sm p-3 md:p-4 flex flex-col justify-center gap-3">
        {children}
      </div>
    </div>
  );
}

function Header({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center justify-between text-[#1e3350]">
      <div className="flex items-center gap-2 font-semibold text-[13px]">
        {icon}
        {title}
      </div>
      <span className="text-[11px] opacity-70">ConvoBuddy</span>
    </div>
  );
}

/* FULLY i18n CARDS */
function MultiCard() {
  const { t } = useTranslation("home");
  const langNames = t("deck.cards.multi.languages", { returnObjects: true }) as Record<string, string>;

  return (
    <CardShell>
      <Header icon={<Icon.Globe />} title={t("deck.cards.multi.title")} />
      <div className="flex flex-wrap gap-2">
        {["en", "hi", "te", "ta", "fr"].map((code) => (
          <span
            key={code}
            className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{ background: BRAND.chipBg, color: BRAND.chipText }}
          >
            {code === "te" ? "తెలుగు" : code === "ta" ? "தமிழ்" : langNames[code] || code.toUpperCase()}
          </span>
        ))}
      </div>
      <div className="grid gap-2">
        <div className="self-start rounded-xl bg-white px-3 py-2 text-[12px] text-[#2a3d52] shadow">
          {t("deck.cards.multi.userExample")}
        </div>
        <div className="self-end max-w-[85%] rounded-xl bg-white px-3 py-2 text-[12px] text-[#2a3d52] shadow">
          {t("deck.cards.multi.botExample")}
        </div>
      </div>
    </CardShell>
  );
}

function VoiceCard() {
  const { t } = useTranslation("home");
  return (
    <CardShell>
      <Header icon={<Icon.Mic />} title={t("deck.cards.voice.title")} />
      <div className="flex items-center gap-3 rounded-xl bg-white px-3 py-2 shadow">
        <div className="grid h-8 w-8 place-items-center rounded-full" style={{ background: "#E8F6F5", color: BRAND.teal }}>
          <Icon.Mic />
        </div>
        <div className="flex h-6 flex-1 items-end gap-[3px]">
          {[5, 12, 8, 18, 10, 16, 7, 14, 9, 6].map((h, i) => (
            <span key={i} style={{ width: 4, height: h, background: "#B6E5E2", borderRadius: 2 }} />
          ))}
        </div>
        <span className="text-[11px] font-semibold text-[#51667a]">{t("deck.cards.voice.listening")}</span>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-white pl-3 pr-1 shadow">
        <input
          className="w-full bg-transparent py-2 text-[12px] outline-none placeholder:text-[#9AA6B2]"
          placeholder={t("deck.cards.voice.inputPlaceholder")}
        />
        <button className="rounded-lg px-3 py-1 text-[12px] font-semibold text-white" style={{ background: BRAND.indigo }}>
          {t("deck.cards.voice.send")}
        </button>
      </div>
      <div className="rounded-lg bg-[#F4F7FE] px-2 py-1 text-[11px] text-[#42566b]">
        {t("deck.cards.voice.description")}
      </div>
    </CardShell>
  );
}

function FlowCard() {
  const { t } = useTranslation("home");
  const bullets = t("deck.cards.flow.bullets", { returnObjects: true }) as string[];
  return (
    <CardShell>
      <Header icon={<Icon.Flow />} title={t("deck.cards.flow.title")} />
      <div className="rounded-xl bg-white p-3 text-[12px] text-[#2a3d52] shadow">
        <div className="mb-2 font-semibold" style={{ color: BRAND.text }}>
          {t("deck.cards.flow.smartRouting")}
        </div>
        <ul className="ml-4 list-disc space-y-1 text-[#3B5563]">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
        <div className="mt-3">
          <div className="h-2 w-full rounded-full bg-[#E8EEF7]">
            <div className="h-2 rounded-full" style={{ width: "68%", background: BRAND.teal }} />
          </div>
          <div className="mt-1 text-right text-[11px] text-[#3B5563]">68%</div>
        </div>
      </div>
      <div className="self-start rounded-lg bg-[#1a2151] px-3 py-1 text-[11px] font-semibold text-[#FFDF7A]">
        {t("deck.cards.flow.integrationHighlight")}
      </div>
    </CardShell>
  );
}

function ResponsesCard() {
  const { t } = useTranslation("home");
  const bullets = t("deck.cards.responses.bullets", { returnObjects: true }) as string[];
  return (
    <CardShell>
      <Header icon={<Icon.Spark />} title={t("deck.cards.responses.title")} />
      <ul className="ml-4 list-disc space-y-1 text-[13px] text-[#3B5563]">
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </CardShell>
  );
}

function SecurityCard() {
  const { t } = useTranslation("home");
  const bullets = t("deck.cards.security.bullets", { returnObjects: true }) as string[];
  return (
    <CardShell>
      <Header icon={<Icon.Shield />} title={t("deck.cards.security.title")} />
      <ul className="ml-4 list-disc space-y-1 text-[13px] text-[#3B5563]">
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </CardShell>
  );
}

function PlugCard() {
  const { t } = useTranslation("home");
  const bullets = t("deck.cards.plug.bullets", { returnObjects: true }) as string[];
  return (
    <CardShell>
      <Header icon={<Icon.Plug />} title={t("deck.cards.plug.title")} />
      <ul className="ml-4 list-disc space-y-1 text-[13px] text-[#3B5563]">
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </CardShell>
  );
}

type CardKey = "multi" | "voice" | "flow" | "responses" | "security" | "plug";
const CARD_RENDER: Record<CardKey, () => ReactElement> = {
  multi: MultiCard,
  voice: VoiceCard,
  flow: FlowCard,
  responses: ResponsesCard,
  security: SecurityCard,
  plug: PlugCard
};

/* Deck Carousel */
const AUTO_MS = 9000;
const TURN_MS = 720;

function DeckCarousel({ width, height }: { width: number; height: number }) {
  const { t } = useTranslation("home");
  const badge = t("deck.badge");
  const keys: CardKey[] = ["multi", "voice", "flow", "responses", "security", "plug"];

  const [order, setOrder] = useState<CardKey[]>(() => keys);
  const [paused, setPaused] = useState(false);
  const [turning, setTurning] = useState<{ key: CardKey; dir: "next" | "prev" } | null>(null);

  const visible = order.slice(-3);

  const advance = () => {
    if (turning) return;
    setTurning({ key: order[order.length - 1], dir: "next" });
    setTimeout(() => {
      setOrder((prev) => [...prev.slice(1), prev[0]]);
      setTurning(null);
    }, TURN_MS);
  };

  const back = () => {
    if (turning) return;
    setTurning({ key: order[order.length - 1], dir: "prev" });
    setTimeout(() => {
      setOrder((prev) => [prev[prev.length - 1], ...prev.slice(0, prev.length - 1)]);
      setTurning(null);
    }, TURN_MS);
  };

  useEffect(() => {
    if (paused || document.hidden) return;
    const id = setInterval(advance, AUTO_MS);
    return () => clearInterval(id);
  }, [paused, order]);

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") advance();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const positions = [
    { x: -26, y: 22, scale: 0.92, z: 10 },
    { x: -13, y: 10, scale: 0.96, z: 20 },
    { x: 0, y: 0, scale: 1.0, z: 30 }
  ] as const;

  const frameWidth = width + 80;
  const shellHeight = height + 160;

  function Slot({ cardKey, slotIndex }: { cardKey: CardKey; slotIndex: 0 | 1 | 2 }) {
    const Pos = positions[slotIndex];
    const Card = CARD_RENDER[cardKey];
    const doTurn = turning && turning.key === visible[2] && slotIndex === 2;
    const dir = turning?.dir ?? "next";
    const origin = dir === "next" ? "right center" : "left center";

    return (
      <motion.div
        className="absolute rounded-[22px]"
        style={{ inset: 0, transformStyle: "preserve-3d", perspective: 1200, zIndex: Pos.z }}
        animate={{
          x: Pos.x,
          y: Pos.y,
          scale: Pos.scale,
          rotateY: doTurn ? [0, dir === "next" ? 18 : -18, 0] : 0,
          skewY: doTurn ? [0, dir === "next" ? 2 : -2, 0] : 0,
          transition: doTurn ? { duration: TURN_MS / 1000, ease: [0.22, 1, 0.36, 1] } : { type: "spring", stiffness: 260, damping: 28 }
        }}
      >
        <div className="h-full w-full" style={{ transformOrigin: origin }}>
          <motion.div
            className="pointer-events-none absolute inset-0 rounded-[22px]"
            style={{
              background: "linear-gradient(100deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.15) 100%)",
              mixBlendMode: "overlay"
            }}
            animate={{ opacity: doTurn ? [0, 0.6, 0] : 0 }}
            transition={{ duration: TURN_MS / 1000 }}
          />
          <Card />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={deckContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full max-w-full overflow-x-hidden"
      style={{ height: shellHeight }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
    >
      <div
        className="relative mx-auto max-w-full rounded-[28px] shadow-[0_30px_70px_rgba(20,30,60,.14)]"
        style={{
          width: `min(100%, ${frameWidth}px)`,
          height: height + 120,
          background: `linear-gradient(180deg, ${BRAND.panelTop} 0%, ${BRAND.panelBottom} 100%)`,
          border: "1px solid rgba(17,24,39,.06)",
          overflow: "hidden"
        }}
      >
        <div className="flex items-center justify-center pt-8" onClick={advance} onContextMenu={(e) => { e.preventDefault(); back(); }}>
          <div className="relative" style={{ width, height, cursor: "pointer" }}>
            <Slot cardKey={visible[0]} slotIndex={0} />
            <Slot cardKey={visible[1]} slotIndex={1} />
            <Slot cardKey={visible[2]} slotIndex={2} />
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-6 flex justify-center">
          <div
            className="rounded-2xl px-6 py-2.5 text-[12px] font-semibold"
            style={{
              background: "#1a2151",
              color: "#FFDF7A",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)"
            }}
          >
            {badge}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const LivePreviewAndWhere = memo(function LivePreviewAndWhere() {
  const { cw, ch } = useDeckTokens();
  const { t } = useTranslation("home");

  return (
    <section className="relative z-10 bg-white py-20" data-animate="fade-in">
      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <div className="flex justify-center">
            <DeckCarousel width={cw} height={ch} />
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 flex flex-col justify-center"
          >
            <h2 className="text-2xl font-semibold text-[#00B7B0]">
              {t("whereHelps.title")}
            </h2>
            <ul className="space-y-3 text-base text-[#414141]">
              {(t("whereHelps.items", { returnObjects: true }) as string[]).map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="fa-solid fa-check mt-1 text-[#00B7B0]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

/* Key Features Magic, NOW FIXED WITH CORRECT KEYS */
function AsteroidField() {
  const asteroids = useMemo(
    () =>
      Array.from({ length: 16 }).map((_, idx) => {
        const ring = 220 + (idx % 4) * 26;
        const size = 8 + (idx % 3) * 3;
        const blur = idx % 5 === 0 ? 1.4 : 0.4;
        return {
          size,
          radius: ring,
          duration: 20 + (idx % 5) * 3,
          delay: idx * 0.45,
          blur,
          glow:
            idx % 2 === 0
              ? "linear-gradient(145deg, rgba(255,255,255,0.85), rgba(255,255,255,0.25))"
              : "linear-gradient(145deg, rgba(223,246,255,0.9), rgba(150,214,255,0.35))"
        };
      }),
    []
  );

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center">
      {asteroids.map((rock, idx) => (
        <span
          key={`asteroid-${idx}`}
          className="animate-asteroid absolute rounded-full opacity-70"
          style={{
            width: rock.size,
            height: rock.size,
            top: "50%",
            left: "50%",
            marginLeft: rock.radius,
            marginTop: -rock.size / 2,
            animationDuration: `${rock.duration}s`,
            animationDelay: `${rock.delay}s`,
            filter: `blur(${rock.blur}px)`,
            transformOrigin: `-${rock.radius}px 0`,
            background: rock.glow,
            boxShadow: "0 12px 24px rgba(15,15,35,0.18)"
          }}
        />
      ))}
    </div>
  );
}

const KeyFeaturesMagic = memo(function KeyFeaturesMagic() {
  const { t } = useTranslation("home");
  // Cards render assembled in their grid from the start — the ambient
  // "floating" state (used only before the first reveal) overlaps grid
  // cells by design, which reads as broken rather than playful on first
  // paint. Starting revealed keeps the nicer part: a spring-reflow shuffle
  // on click, always between two clean, readable grid states.
  const [revealed, setRevealed] = useState(true);
  const [order, setOrder] = useState(() => FEATURE_CONFIG.map((_, i) => i));
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scatter = useMemo(() => {
    const jitter = (delta: number) => (Math.random() - 0.5) * delta;
    return FEATURE_CONFIG.map((_, idx) => {
      const angle = (idx / FEATURE_CONFIG.length) * Math.PI * 2;
      const radiusX = 200;
      const radiusY = 140;
      const phase = Math.sin(angle * 1.35);
      return {
        x: Math.cos(angle) * radiusX + jitter(24),
        y: Math.sin(angle) * radiusY + jitter(18),
        z: phase * 90,
        rotateX: phase * 18,
        rotateY: Math.cos(angle) * 18,
        scale: 0.95 + phase * 0.08,
        driftSpeed: 3 + (idx % 4) * 0.6,
        driftRadiusX: 32 + (idx % 3) * 18,
        driftRadiusY: 28 + ((idx + 1) % 3) * 16,
        driftRadiusZ: 24 + (idx % 4) * 12,
        wobble: 0.35 + ((idx % 4) * 0.08)
      };
    });
  }, []);

  const shuffle = () => {
    setOrder((prev) => {
      const arr = [...prev];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
  };

  useEffect(() => {
    if (!revealed || hovered) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }
    timeoutRef.current = setTimeout(shuffle, 10000);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [revealed, order, hovered]);

  return (
    <section
      className="relative z-10 isolate bg-[#E5F8F7] py-24 cursor-pointer select-none overflow-hidden"
      onClick={() => (revealed ? shuffle() : setRevealed(true))}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ perspective: 1800, minHeight: "620px" }}
    >
      <AsteroidField />
      <div className="relative z-20 mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl font-bold text-[#2F2A7A] sm:text-5xl">{t("keyFeatures.title")}</h2>
        <p className="mt-4 text-lg text-[#414141] font-medium">{t("keyFeatures.subtitle")}</p>
      </div>

      <div className="relative mt-12 mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
        <div
          className="grid gap-6 sm:grid-cols-[repeat(2,minmax(0,1fr))] lg:grid-cols-[repeat(3,minmax(0,1fr))]"
          style={{ transformStyle: "preserve-3d" }}
        >
          {order.map((idx, gridIndex) => {
            const f = FEATURE_CONFIG[idx];
            const s = scatter[idx];

            // THIS WAS THE BUG, Fixed: Now uses correct key like "features.multilingual.title"
            const title = t(`features.${f.key}.title`);
            const description = t(`features.${f.key}.description`);

            const floatAnim = {
              x: [s.x - s.driftRadiusX * 0.35, s.x + s.driftRadiusX * 0.35],
              y: [s.y - s.driftRadiusY * 0.35, s.y + s.driftRadiusY * 0.35],
              z: [s.z - s.driftRadiusZ * 0.3, s.z + s.driftRadiusZ * 0.3],
              rotateX: [s.rotateX - 8, s.rotateX + 8],
              rotateY: [s.rotateY - 8, s.rotateY + 8],
              scale: [s.scale * 0.96, s.scale * 1.04],
              opacity: 0.9
            };

            const initial = { x: s.x, y: s.y, z: s.z, rotateX: s.rotateX, rotateY: s.rotateY, scale: s.scale, opacity: 0.7 };
            const animate = revealed ? { x: 0, y: 0, z: 0, rotateX: 0, rotateY: 0, scale: 1, opacity: 1 }
              : hovered ? { x: s.x, y: s.y, z: s.z, rotateX: s.rotateX, rotateY: s.rotateY, scale: s.scale, opacity: 0.9 }
                : floatAnim;

            const transition: Transition = revealed
              ? { type: "spring", stiffness: 120, damping: 20, delay: gridIndex * 0.08 }
              : hovered ? { type: "spring", stiffness: 60, damping: 30 }
                : { duration: 15 / s.driftSpeed, repeat: Infinity, repeatType: "mirror", ease: "easeInOut", delay: gridIndex * 0.3 };

            return (
              <motion.div
                key={`${f.key}-${idx}`}
                layout
                initial={initial}
                animate={animate}
                transition={transition}
                className="flex h-full flex-col gap-3 rounded-3xl border border-[#E5E5E5] bg-white p-6 text-left shadow-[0_18px_38px_rgba(15,15,35,0.08)] backdrop-blur-sm"
                style={{ transformStyle: "preserve-3d" }}
                whileHover={{ scale: 1.06, z: 60, boxShadow: "0 30px 70px rgba(15,15,35,0.15)" }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EBE8FF] text-[#2F2A7A]">
                  <i className={`fa-solid ${f.icon} text-lg`} aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold text-[#414141] leading-snug break-words">
                  {title}
                </h3>
                <p className="text-sm text-[#6E6E6E] leading-relaxed break-words">
                  {description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

/* About & Form, unchanged */
const AboutUsSection = memo(function AboutUsSection() {
  const { t } = useTranslation("home");
  return (
    <motion.section
      className="relative z-10 bg-[#F9F9F9] py-24"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="space-y-6">
            <h2 className="text-3xl font-bold text-[#2F2A7A] sm:text-4xl">{t("about.title")}</h2>
            <p className="text-lg text-[#414141]">{t("about.body")}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }} className="space-y-6">
            <h3 className="text-2xl font-semibold text-[#00B7B0]">{t("about.whyTitle")}</h3>
            <ul className="space-y-3 text-base text-[#414141]">
              {(t("about.reasons", { returnObjects: true }) as string[]).map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="fa-solid fa-check mt-1 text-[#00B7B0]" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <a href="https://kirusa.ai/about" className="inline-flex items-center gap-2 rounded-full border border-[#2F2A7A] px-6 py-3 text-sm font-semibold text-[#2F2A7A] transition hover:-translate-y-0.5 hover:bg-[#EBE8FF]">
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              {t("about.learnMore")}
            </a>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
});

export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("home");

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState<string>("");


  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setFeedback("");

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      setStatus("error");
      setFeedback(t("form.messages.csrfMissing"));
      return;
    }

    try {
      const res = await fetch("/request-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        credentials: "include",
        body: JSON.stringify({ ...form, csrftoken: csrfToken })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t("form.messages.submitError"));
      setStatus("success");
      setFeedback(json.message || t("form.messages.success"));
      setForm({ name: "", email: "", phone: "", company: "" });
    } catch {
      setStatus("error");
      setFeedback(t("form.messages.submitGenericError"));
    }
  };

  return (
    <Layout>
      <ConvoHero />
      <LivePreviewAndWhere />
      <KeyFeaturesMagic />
      <AboutUsSection />
      <section className="relative z-10 bg-white py-24" data-animate="fade-in">
        <div className="mx-auto grid max-w-[1380px] gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:px-8">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-[#414141] sm:text-4xl">{t("cta.title")}</h2>
            <p className="max-w-xl text-base text-[#6E6E6E]">{t("cta.subtitle")}</p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/signup")}
                className="rounded-full border border-[#D81F26] px-6 py-3 text-sm font-semibold text-[#D81F26] hover:bg-[#D81F26] hover:text-white transition text-center whitespace-normal break-words"
              >
                <span className="inline-flex items-center gap-2"><i className="fa-solid fa-tags" /> {t("cta.primaryCta")}</span>
              </button>
              <a
                href="mailto:support-convobuddy@kirusa.com"
                className="rounded-full border border-[#E5E5E5] px-6 py-3 text-sm font-semibold text-[#414141] hover:bg-[#F9F9F9] transition text-center whitespace-normal break-words"
              >
                <span className="inline-flex items-center gap-2"><i className="fa-solid fa-circle-question" /> {t("cta.secondaryCta")}</span>
              </a>
            </div>
          </div>

          <div className="rounded-[28px] border border-[#E5E5E5] bg-white p-6 shadow-[0_24px_48px_rgba(15,15,35,0.08)]">
            <form className="grid gap-4" onSubmit={handleSubmit}>
              {(["name", "email", "phone", "company"] as const).map((field) => (
                <div key={field} className="space-y-2">
                  <label className="text-sm font-semibold text-[#414141]" htmlFor={field}>{t(`form.labels.${field}`)}</label>
                  <input
                    className="w-full rounded-xl border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm text-[#414141] shadow-sm outline-none transition focus:border-[#D81F26] focus:ring-2 focus:ring-[#D81F26]/20"
                    id={field}
                    name={field}
                    type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                    required={field !== "phone" && field !== "company"}
                    value={form[field]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
                  />
                </div>
              ))}

              {status !== "idle" && feedback && (
                <p className={`rounded-xl px-4 py-3 text-sm ${status === "success" ? "bg-[#E6F4EA] text-[#2E7D32]" : "bg-[#FDECEA] text-[#C62828]"}`}>
                  {feedback}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-2 rounded-full bg-[#D81F26] px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-[#B71C1C] disabled:opacity-70 transition"
              >
                <span className="inline-flex items-center gap-2">
                  <i className="fa-solid fa-paper-plane" />
                  {status === "loading" ? t("form.button.loading") : t("form.button.idle")}
                </span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
